/**
 * Sidebar conversation drag-reorder hook. Extracted verbatim
 * (behavior-preserving) from layout/Sidebar.tsx — owns the conversation DnD
 * state (`draggedIndex`, `tempConversationOrder`), the temp-order reconcile
 * effect, and the four pointer handlers (`handleDragStart`, `handleDragOver`,
 * `handleDrop`, `handleDragEnd`).
 *
 * Hook-order safety: this hook is placed right after `userConversations` is
 * derived and before the consumers that read `tempConversationOrder` (the
 * `displayedConversations` memo, the context-menu pin handler). Internally it
 * runs `useState` ×2 → `useEffect` (reconcile). Nothing between the old state
 * site and here read `tempConversationOrder` as a hook dependency except the
 * reconcile effect (which moves with it), so the render-to-render hook order
 * stays stable. The reconcile effect's deps `[tempConversationOrder,
 * orderedUserConversations]` and body are byte-identical (the inline
 * `sameOrderById` useCallback is replaced by the pure `sameOrderById` kernel,
 * dropping it from the dep array — the kernel is a module-level constant, so
 * the effect's behavior and re-run conditions are unchanged).
 *
 * The live splice-reorder is delegated to the `reorderByIndex` kernel and the
 * cache-caught-up check to the `sameOrderById` kernel (both verbatim ports
 * pinned by dragReorder.test.ts).
 */

import type React from 'react';
import { useEffect, useState } from 'react';
import type { useReorderConversationsMutation } from '@/features/conversations/hooks/useReorderConversationsMutation';
import type { Conversation } from '@/types/conversations';
import { reorderByIndex, sameOrderById } from '../dragReorder';

interface UseDragReorderArgs {
  userConversations: Conversation[];
  orderedUserConversations: Conversation[];
  reorderMutation: ReturnType<typeof useReorderConversationsMutation>;
}

export function useDragReorder({
  userConversations,
  orderedUserConversations,
  reorderMutation,
}: UseDragReorderArgs) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [tempConversationOrder, setTempConversationOrder] = useState<Conversation[] | null>(null);

  // Only clear temporary order once the query cache has caught up to the committed order.
  useEffect(() => {
    if (!tempConversationOrder) return;
    if (sameOrderById(tempConversationOrder, orderedUserConversations)) {
      setTempConversationOrder(null);
    }
  }, [tempConversationOrder, orderedUserConversations]);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === index) {
      return;
    }

    // Create new order with item moved to target position (live reordering)
    const conversations = tempConversationOrder ?? userConversations;
    const newOrder = reorderByIndex(conversations, draggedIndex, index);

    setTempConversationOrder(newOrder);
    setDraggedIndex(index); // Update dragged index to new position
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();

    setDraggedIndex(null);

    // Save the final order if there were changes
    if (tempConversationOrder) {
      const newOrder = tempConversationOrder;

      // Optimistic cache update happens in onMutate (immediate)
      reorderMutation.mutate(newOrder.map((conversation) => conversation.id), {
        onError: () => {
          // On error, clear temp order to show rolled-back cache state
          console.error('[Desktop Reorder] Failed to save conversation order');
          setTempConversationOrder(null);
        },
      });
    } else {
      setTempConversationOrder(null);
    }
  };

  const handleDragEnd = () => {
    // Reset drag state if drag was cancelled
    setDraggedIndex(null);
    setTempConversationOrder(null);
  };

  return {
    draggedIndex,
    tempConversationOrder,
    setTempConversationOrder,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
  };
}
